// src/services/questionnaireDimensionRiskRules.ts
import { api } from "@/lib/apiClient";
import type {
  QuestionnaireDimensionRiskRule,
  QuestionnaireDimensionRiskRuleLookups,
  QuestionnaireDimensionRiskRuleUpsertIn,
} from "@/types/questionnaireDimensionRiskRules";
import { listDimensions } from "@/services/dimensions";

export async function listQuestionnaireDimensionRiskRules(params?: {
  dimension_id?: string;
  is_active?: boolean;
}) {
  return api.get<QuestionnaireDimensionRiskRule[]>("/dimension-risk-rules", {
    params: {
      dimension_id: params?.dimension_id || undefined,
      is_active:
        typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}

export async function getQuestionnaireDimensionRiskRule(ruleId: string) {
  return api.get<QuestionnaireDimensionRiskRule>(`/dimension-risk-rules/${ruleId}`);
}

export async function listQuestionnaireDimensionRiskRuleLookups() {
  const [dimensions, lookups] = await Promise.all([
    listDimensions({ is_active: true }),
    api.get<Omit<QuestionnaireDimensionRiskRuleLookups, "dimensions">>(
      "/dimension-risk-rules/lookups",
    ),
  ]);

  return {
    dimensions: dimensions.map((item) => ({
      id: item.id,
      code: item.code,
      name: item.name,
      is_active: item.is_active,
    })),
    risks: lookups.risks,
    probability_levels: lookups.probability_levels,
    severity_levels: lookups.severity_levels,
  } satisfies QuestionnaireDimensionRiskRuleLookups;
}

export async function createQuestionnaireDimensionRiskRule(
  payload: QuestionnaireDimensionRiskRuleUpsertIn,
) {
  return api.post<QuestionnaireDimensionRiskRule>("/dimension-risk-rules", payload);
}

export async function updateQuestionnaireDimensionRiskRule(
  ruleId: string,
  payload: QuestionnaireDimensionRiskRuleUpsertIn,
) {
  return api.put<QuestionnaireDimensionRiskRule>(
    `/dimension-risk-rules/${ruleId}`,
    payload,
  );
}

export async function setQuestionnaireDimensionRiskRuleStatus(
  ruleId: string,
  is_active: boolean,
) {
  return api.patch<QuestionnaireDimensionRiskRule>(
    `/dimension-risk-rules/${ruleId}/status`,
    { is_active },
  );
}

export async function deleteQuestionnaireDimensionRiskRule(ruleId: string) {
  return api.delete<void>(`/dimension-risk-rules/${ruleId}`);
}
